import {
  errorCodeHandler,
  errorMessageHandler,
  errorHandleNoBody,
  setTokens,
  unsetTokens,
  acceptInvitation,
  slackSignUp,
} from "apis";
import queryString from "query-string";
import { isLoading, displayError, SecureLocalStorage } from "helpers";
import {
  setUserApplicationsPermissions,
  unSetUserApplicationsPermissions,
} from "./userApplicationsPermissions";
import { setApplication } from "./activeApp";
import { cloneDeep } from "lodash";
import { refreshAuthToken, unauthorizedLogout } from "../../apis";
import { updateCustomerInfo } from "./cartActions";

// Authentication Actions
export const setUser = (user) => ({
  type: "SET_USER_STATUS",
  user,
});

const unSetUser = () => ({
  type: "UNSET_USER_STATUS",
});

export const acceptInvitationSignUp =
  (userInvitationToken, password) => async (dispatch) => {
    const user = await acceptInvitation(userInvitationToken, password);
    dispatch(setUser(user));
  };

export const signup =
  ({ invitationToken, ...user }) =>
  (dispatch) => {
    let url = __BASE_API_URL__ + `/user?deviceType=0`;
    if (invitationToken) url += `&invitationToken=${invitationToken}`;

    isLoading(true);

    return new Promise((resolve, reject) => {
      return fetch(url, {
        method: "POST",
        headers: new Headers({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify(user),
      })
        .then(errorCodeHandler)
        .then(setTokens)
        .then((res) => res.json())
        .then(errorMessageHandler)
        .then((json) => {
          let {
            admin,
            createdBy,
            createdDate,
            favoritedApplications,
            modifiedBy,
            modifiedDate,
            notificationApplications,
            password,
            retired,
            savedApplications,
            ...user
          } = json;

          SecureLocalStorage.setItem("userId", user.id);
          slackSignUp(user.firstName, user.lastName);
          dispatch(setUser(user));
          dispatch(
            updateCustomerInfo({
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              phone: user.phone,
            })
          );
          isLoading(false);
          return resolve(user);
        })
        .catch((error) => {
          isLoading(false);
          displayError(error);
          reject(error);
        });
    });
  };

export const login = (user, isWeb) => (dispatch) => {
  const url = __BASE_API_URL__ + `/login?deviceType=0`;

  isLoading(true);

  return new Promise((resolve, reject) => {
    fetch(url, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(user),
    })
      .then(errorCodeHandler)
      .then(setTokens)
      .then((res) => res.json())
      .then(errorMessageHandler)
      .then((json) => {
        let {
          admin,
          createdBy,
          createdDate,
          favoritedApplications,
          modifiedBy,
          modifiedDate,
          notificationApplications,
          password,
          retired,
          savedApplications,
          userApplications,
          ...user
        } = json;

        SecureLocalStorage.setItem("userId", user.id);
        dispatch(setUserApplicationsPermissions(userApplications));
        dispatch(setUser(user));

        if (isWeb) {
          dispatch(
            updateCustomerInfo({
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              phone: user.phone,
            })
          );
          isLoading(false);
        }
        return resolve(user);
      })
      .catch((error) => {
        isLoading(false);
        console.error(error);
        displayError(error);
        reject(error);
      });
  });
};

export const webFeatureLogin = (appId, userId) => (dispatch) => {
  const userId = SecureLocalStorage.getItem("userId");

  let url = `${__BASE_API_URL__}/applications/${appId}/webpage?userId=${userId}`;
  if (!userId) url = `${__BASE_API_URL__}/applications/${appId}/webpage`;

  isLoading(true);

  return new Promise((resolve, reject) => {
    fetch(url, {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(errorCodeHandler)
      .then(setTokens)
      .then((res) => res.json())
      .then(errorMessageHandler)
      .then((json) => {
        const { application, user } = json;
        // console.log("######## GOT WEB OBJECT AND USER OBJECT");
        // console.dir(json);
        SecureLocalStorage.setItem("userId", user.id);

        dispatch(setUser(user));

        // DEBUG
        // SecureLocalStorage.setItem("userId", user.id);

        const view = JSON.parse(
          decodeURI(
            application.publishedView !== null
              ? application.publishedView
              : application.unpublishedView
          )
        );
        const appState = { view, ...application };
        dispatch(setApplication(cloneDeep(appState)));
        isLoading(false);
        return resolve(json);
      })
      .catch((error) => {
        isLoading(false);
        if (error === "PerformRefreshToken") {
          refreshAuthToken()
            .then(() => {
              webFeatureLogin(appId);
            })
            .catch(reject);
        } else if (error === "PerformUnauthorizedToken") {
          displayError("Session Expired");
          unauthorizedLogout();
        } else {
          displayError(error);
          reject(error);
        }
      });
  });
};

export const logOut = () => (dispatch) => {
  SecureLocalStorage.removeItem("userId");
  isLoading(false);
  unsetTokens();
  dispatch(unSetUser());
  dispatch(unSetUserApplicationsPermissions());
  // dispatch(
  //   updateCustomerInfo({
  //     email: "",
  //     firstName: "",
  //     lastName: "",
  //     phone: "",
  //   })
  // );
};

export const resetPassword = (resetPasswordToken, password) => (dispatch) => {
  isLoading(true);
  const queryParams = queryString.stringify({
    resetPasswordToken,
    password,
  });
  const url = __BASE_API_URL__ + `/resetPassword?${queryParams}`;
  return new Promise((resolve, reject) => {
    return fetch(url, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(errorHandleNoBody)
      .then(errorMessageHandler)
      .then((json) => {
        isLoading(false);
        resolve();
      })
      .catch((error) => {
        isLoading(false);
        displayError(error);
        reject(error);
      });
  });
};

export const forgotPassword = (email) => (dispatch) => {
  isLoading(true);
  const url = __BASE_API_URL__ + `/forgotPassword?email=${email}`;
  return new Promise((resolve, reject) => {
    return fetch(url, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
      }),
    })
      .then(errorHandleNoBody)
      .then(errorMessageHandler)
      .then((json) => {
        console.log("success");
        isLoading(false);
        resolve();
      })
      .catch((error) => {
        isLoading(false);
        displayError(error);
        reject(error);
      });
  });
};
